import React from 'react';
import Link from 'next/link';
import { ArrowRight, CloudSnow } from 'lucide-react';
import { cn } from '@/util/cn';

// Sidebar snowfall card: small eyebrow + arrow CTA, a hero annual
// number, and a stacked monthly bar chart. The bars are pure CSS:
// each row is a relative container with an absolutely positioned
// secondary/15 fill whose width encodes value/max.

export interface SnowfallMonth {
  /** Short month label, e.g. "NOV" — rendered as-is. */
  month: React.ReactNode;
  /** Numeric value used for the bar-width ratio. */
  value: number;
  /** Optional formatted display string. Falls back to value. */
  display?: React.ReactNode;
}

export interface SnowfallCardProps {
  title: React.ReactNode;
  annualValue: React.ReactNode;
  annualLabel: React.ReactNode;
  monthly: SnowfallMonth[];
  /** When set, renders an arrow CTA in the header that links here. */
  href?: string;
  ariaLabel?: string;
  className?: string;
}

export const SnowfallCard = ({
  title,
  annualValue,
  annualLabel,
  monthly,
  href,
  ariaLabel,
  className,
}: SnowfallCardProps) => {
  const max = Math.max(1, ...monthly.map((m) => m.value));

  return (
    <section className={cn('rounded-md border border-border bg-card p-4', className)}>
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="inline-flex items-center gap-1.5">
          <CloudSnow size={14} className="text-secondary" />
          <span className="text-xs font-bold uppercase tracking-widest text-secondary">
            {title}
          </span>
        </div>
        {href && (
          <Link
            href={href}
            aria-label={ariaLabel}
            className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm transition hover:bg-primary/90"
          >
            <ArrowRight size={14} />
          </Link>
        )}
      </div>

      <div className="mb-4 border-b border-border pb-4">
        <div className="font-heading text-3xl font-bold leading-none text-secondary">
          {annualValue}
        </div>
        <div className="mt-2 text-xs font-bold uppercase tracking-widest text-muted-foreground">
          {annualLabel}
        </div>
      </div>

      <ul className="m-0 list-none space-y-1.5 p-0">
        {monthly.map((m, i) => {
          const pct = Math.max(18, (m.value / max) * 100);
          return (
            <li
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              className="relative h-8"
            >
              <div
                className="absolute inset-y-0 left-0 rounded bg-secondary/15"
                style={{ width: `${pct}%` }}
              />
              <div className="relative flex h-full items-center justify-between px-2.5">
                <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  {m.month}
                </span>
                <span className="text-sm font-bold tabular-nums text-secondary">
                  {m.display ?? m.value}
                </span>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
};
